import { Head } from "@inertiajs/react";
import { ArrowRight, Check, CheckCircle2, Circle, Eye, FileText, Github, Server } from "lucide-react";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CodeExamplesCard } from "@/components/docs/code-examples-card";
import { FinalCta } from "@/components/final-cta";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { OG_PILOT_CURSOR_PROMPT, buildCursorPromptLink } from "@/lib/cursor-deeplink";
import { cn } from "@/lib/utils";

interface Section {
  id: string;
  title: string;
  content: string;
}

interface Parameter {
  name: string;
  type: string;
  required: boolean;
  default?: string;
  description: string;
}

interface Template {
  name: string;
  description: string;
  default?: boolean;
  parameters: Parameter[];
}

interface Props {
  sections: Section[];
  templates: Template[];
}

interface GitHubContributionDay {
  date: string;
  count: number;
}

const GITHUB_GRAPH_ROWS = 7;
const GITHUB_FIXED_COLUMNS = 26;
const GITHUB_VISIBLE_DAYS = 175;
const GITHUB_DEFAULT_ACCENT_COLOR = "#19e67c";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizeHexColor(value: string | undefined, fallback: string) {
  const match = value?.trim().match(/^#?([a-fA-F0-9]{3}|[a-fA-F0-9]{6})$/);
  if (!match) return fallback;
  const normalized =
    match[1].length === 3
      ? match[1]
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : match[1];
  return `#${normalized.toLowerCase()}`;
}

function mixHexColors(baseHex: string, accentHex: string, accentRatio: number) {
  const base = baseHex.replace("#", "");
  const accent = accentHex.replace("#", "");
  const toRgb = (hex: string) => [0, 2, 4].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16));
  const [r1, g1, b1] = toRgb(base);
  const [r2, g2, b2] = toRgb(accent);
  const channel = (from: number, to: number) => Math.round(from * (1 - accentRatio) + to * accentRatio);
  const rgb = [channel(r1, r2), channel(g1, g2), channel(b1, b2)];
  return `#${rgb.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function buildGitHubColorScale(accentColor: string, bgColor: string, textColor: string) {
  const normalizedAccent = normalizeHexColor(accentColor, GITHUB_DEFAULT_ACCENT_COLOR);
  const base = mixHexColors(bgColor, textColor, 0.06);

  return [
    base,
    mixHexColors(base, normalizedAccent, 0.4),
    mixHexColors(base, normalizedAccent, 0.6),
    mixHexColors(base, normalizedAccent, 0.8),
    normalizedAccent,
  ];
}

function getMondayBasedDayOfWeek(date: Date) {
  const day = date.getDay();
  return day === 0 ? 6 : day - 1;
}

function getGitHubIntensityLevel(count: number, maxCount: number) {
  if (count <= 0 || maxCount <= 0) return 0;
  const ratio = count / maxCount;
  if (ratio > 0.75) return 4;
  if (ratio > 0.5) return 3;
  if (ratio > 0.25) return 2;
  return 1;
}

function buildGitHubSampleContributions() {
  // Keep docs payload compact: 10 non-zero points. Missing days are auto-filled.
  const sparsePoints: [number, number][] = [
    [36, 2],
    [31, 4],
    [27, 1],
    [22, 3],
    [18, 5],
    [14, 2],
    [10, 4],
    [7, 1],
    [3, 3],
    [0, 2],
  ];

  return sparsePoints
    .map(([daysAgo, count]) => {
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);
      return {
        date: date.toISOString().slice(0, 10),
        count,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

const GITHUB_SAMPLE_CONTRIBUTIONS = buildGitHubSampleContributions();
const GITHUB_SAMPLE_CONTRIBUTIONS_JSON = JSON.stringify(GITHUB_SAMPLE_CONTRIBUTIONS, null, 2);
const SUPPORTED_IMAGE_TYPES = ["jpeg", "png", "webp", "gif"] as const;

// Default values for each template
// Using DiceBear shapes API for abstract logo placeholders (free, no attribution required)
// Using Unsplash for hero images (free, no attribution required for most uses)
const TEMPLATE_DEFAULTS: Record<string, Record<string, string>> = {
  page: {
    title: "OG Pilot",
    description: "Generate beautiful Open Graph images programmatically",
    bg_color: "#1a1a2e",
    text_color: "#eaeaea",
    logo_url:
      "https://api.dicebear.com/7.x/shapes/svg?seed=acmecorp&backgroundColor=transparent&shape1Color=eaeaea&shape2Color=a0a0a0&shape3Color=808080",
    image_url: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80",
  },
  blog_post: {
    title: "How to Build A Beautiful Open Graph Image",
    bg_color: "#0f172a",
    text_color: "#f8fafc",
    logo_url:
      "https://api.dicebear.com/7.x/shapes/svg?seed=techblog&backgroundColor=transparent&shape1Color=f8fafc&shape2Color=94a3b8&shape3Color=64748b",
    image_url: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&q=80",
    author_name: "Jane Smith",
    author_avatar_url: "https://api.dicebear.com/7.x/avataaars/svg?seed=JaneSmith",
    publish_date: new Date().toISOString().split("T")[0],
  },
  podcast: {
    title: "Episode 42: The Future of Open Graph Images",
    bg_color: "#18181b",
    text_color: "#fafafa",
    logo_url:
      "https://api.dicebear.com/7.x/shapes/svg?seed=podcastshow&backgroundColor=transparent&shape1Color=fafafa&shape2Color=a1a1aa&shape3Color=71717a",
    image_url: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=800&q=80",
    episode_date: new Date().toISOString().split("T")[0],
  },
  product: {
    title: "Premium Wireless Headphones",
    bg_color: "#ffffff",
    text_color: "#171717",
    logo_url:
      "https://api.dicebear.com/7.x/shapes/svg?seed=audiostore&backgroundColor=transparent&shape1Color=171717&shape2Color=525252&shape3Color=a3a3a3",
    image_url: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=800&q=80",
    unique_selling_point: "50% Off Today",
  },
  event: {
    title: "OG Image Conference 2026",
    bg_color: "#7c3aed",
    text_color: "#ffffff",
    logo_url:
      "https://api.dicebear.com/7.x/shapes/svg?seed=techconf&backgroundColor=transparent&shape1Color=ffffff&shape2Color=c4b5fd&shape3Color=a78bfa",
    image_url: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80",
    event_date: "March 15-17, 2026",
    event_location: "San Francisco, CA",
  },
  book: {
    title: "The Art of Open Graph Images",
    description: "A journey through code and creativity",
    bg_color: "#1e293b",
    text_color: "#f1f5f9",
    logo_url:
      "https://api.dicebear.com/7.x/shapes/svg?seed=publisher&backgroundColor=transparent&shape1Color=f1f5f9&shape2Color=94a3b8&shape3Color=64748b",
    image_url: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=800&q=80",
    author_name: "RaulOnRails",
    series_number: "1",
  },
  portfolio: {
    title: "Sarah's Design Portfolio",
    description: "Creative Director & UI/UX Designer",
    bg_color: "#fef3c7",
    text_color: "#78350f",
    logo_url: "https://api.dicebear.com/7.x/avataaars/svg?seed=JaneSmith",
    image_url: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80",
  },
  company: {
    title: "Acme Industries",
    description: "12 open roles",
    bg_color: "#ffffff",
    text_color: "#0600d8",
    logo_url: "https://calmcompanies.club/calm-co.png",
    company_logo_url:
      "https://api.dicebear.com/7.x/shapes/svg?seed=acmebrand&backgroundColor=transparent&shape1Color=0600d8&shape2Color=00ad12&shape3Color=71717a",
  },
  github: {
    title: "sunergos-ro",
    description: "328 code changes in the last 7 days.",
    bg_color: "#0d1117",
    text_color: "#f0f6fc",
    accent_color: GITHUB_DEFAULT_ACCENT_COLOR,
    contributions: GITHUB_SAMPLE_CONTRIBUTIONS_JSON,
  },
};

// Font families per template type (matches backend social_image_preview.rb)
const TEMPLATE_FONTS: Record<string, { heading: string; body: string }> = {
  page: {
    heading: "Montserrat, sans-serif",
    body: "Open Sans, sans-serif",
  },
  blog_post: {
    heading: "Playfair Display, serif",
    body: "Merriweather, serif",
  },
  podcast: {
    heading: "Nunito, sans-serif",
    body: "Karla, sans-serif",
  },
  product: {
    heading: "Oswald, sans-serif",
    body: "Source Sans 3, sans-serif",
  },
  event: {
    heading: "Playfair Display, serif",
    body: "Montserrat, sans-serif",
  },
  book: {
    heading: "Cinzel, serif",
    body: "Merriweather, serif",
  },
  portfolio: {
    heading: "Raleway, sans-serif",
    body: "Lato, sans-serif",
  },
  company: {
    heading: "Raleway, sans-serif",
    body: "Lato, sans-serif",
  },
  github: {
    heading: "Montserrat, sans-serif",
    body: "Open Sans, sans-serif",
  },
};

// Font stylesheet URLs for docs preview (mirrors SocialImagePreview#font_bunny_url).
const TEMPLATE_FONT_URLS: Record<string, string> = {
  page: "https://fonts.bunny.net/css?family=montserrat:700|open-sans:400",
  blog_post: "https://fonts.bunny.net/css?family=playfair-display:700|merriweather:400",
  podcast: "https://fonts.bunny.net/css?family=nunito:700|karla:400,600",
  product: "https://fonts.bunny.net/css?family=oswald:700|source-sans-3:400,600",
  event: "https://fonts.bunny.net/css?family=playfair-display:400|montserrat:400,600",
  book: "https://fonts.bunny.net/css?family=cinzel:700|merriweather:400",
  portfolio: "https://fonts.bunny.net/css?family=raleway:700|lato:400",
  company: "https://fonts.bunny.net/css?family=raleway:700|lato:400",
  github: "https://fonts.bunny.net/css?family=montserrat:700|open-sans:400",
};

const TEMPLATE_FONT_STYLESHEETS = Array.from(new Set(Object.values(TEMPLATE_FONT_URLS)));

// Landing page types that show description (matches OpenaiService::LANDING_PAGE_TYPES)
const LANDING_PAGE_TYPES = ["page", "portfolio", "company"];

const SDK_VERSIONS = {
  ruby: "0.4.1",
  javascript: "0.4.2",
  python: "0.4.1",
} as const;

const HERO_HIGHLIGHTS = [
  { emoji: "🔐", text: "Sign JWTs locally so your API key never leaves your server." },
  { emoji: "⚡️", text: "One URL generates branded OG images for every page." },
  { emoji: "👀", text: "Live previews help you validate layouts before shipping." },
];

const HERO_LINKS = [
  { label: "View templates", href: "#templates" },
  { label: "Code examples", href: "#code-examples" },
  { label: "Install MCP", href: "/docs/mcp" },
];

const FRAMEWORK_GUIDES = [
  { label: "Next.js", href: "/nextjs-og-image-generator" },
  { label: "Rails", href: "/rails-og-image-generator" },
  { label: "Astro", href: "/astro-og-image-generator" },
];

const CARD_SURFACE_CLASSNAME =
  "[&_[data-slot=card]]:border-orange-200/60 [&_[data-slot=card]]:bg-white/95 dark:[&_[data-slot=card]]:border-orange-500/30 dark:[&_[data-slot=card]]:bg-gray-900/85";

function parseGitHubContributions(value: string | undefined) {
  if (!value) return [];

  try {
    const parsed: unknown = JSON.parse(value);
    const rawDays = Array.isArray(parsed)
      ? parsed
      : isRecord(parsed) && ("date" in parsed || "count" in parsed)
        ? [parsed]
        : isRecord(parsed)
          ? Object.entries(parsed).map(([date, count]) => ({ date, count }))
          : [];

    const normalizedDays = rawDays
      .map((day) => {
        if (!isRecord(day)) return null;

        const dateText = typeof day.date === "string" ? day.date.trim() : "";
        const parsedDate = new Date(dateText);
        if (!dateText || Number.isNaN(parsedDate.getTime())) return null;

        const rawCount = day.count;
        const parsedCount =
          typeof rawCount === "number" ? rawCount : typeof rawCount === "string" ? Number.parseInt(rawCount, 10) : 0;

        return {
          date: parsedDate.toISOString().slice(0, 10),
          count: Number.isNaN(parsedCount) ? 0 : Math.max(parsedCount, 0),
        };
      })
      .filter((day): day is GitHubContributionDay => Boolean(day))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-366);

    if (normalizedDays.length === 0) return [];

    const countsByDate = new Map<string, number>();
    normalizedDays.forEach((day) => {
      countsByDate.set(day.date, Math.max(countsByDate.get(day.date) ?? 0, day.count));
    });

    const lastDate = new Date(normalizedDays[normalizedDays.length - 1].date);
    const startDate = new Date(lastDate);
    startDate.setDate(startDate.getDate() - (GITHUB_VISIBLE_DAYS - 1));

    const filledDays: GitHubContributionDay[] = [];
    const cursor = new Date(startDate);
    while (cursor <= lastDate) {
      const date = cursor.toISOString().slice(0, 10);
      filledDays.push({
        date,
        count: countsByDate.get(date) ?? 0,
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    return filledDays;
  } catch {
    return [];
  }
}

function computeGitHubTotalChangesLastWeek(contributions: GitHubContributionDay[]) {
  return contributions.slice(-7).reduce((sum, day) => sum + Math.max(day.count, 0), 0);
}

function GitHubContributionGraph({ days, colorScale }: { days: GitHubContributionDay[]; colorScale: string[] }) {
  const orderedDays = [...days]
    .map((day) => ({
      date: new Date(day.date),
      count: Math.max(day.count, 0),
    }))
    .filter((day) => !Number.isNaN(day.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (orderedDays.length === 0) {
    return null;
  }

  const maxCount = orderedDays.reduce((max, day) => Math.max(max, day.count), 0);
  let firstVisibleDay = orderedDays[0];
  let startDayOfWeek = getMondayBasedDayOfWeek(firstVisibleDay.date);
  const maxVisibleDays = Math.max(GITHUB_FIXED_COLUMNS * GITHUB_GRAPH_ROWS - startDayOfWeek, 1);
  const visibleDays = orderedDays.slice(-maxVisibleDays);
  firstVisibleDay = visibleDays[0];
  startDayOfWeek = getMondayBasedDayOfWeek(firstVisibleDay.date);
  const trailingCells = Math.max(GITHUB_FIXED_COLUMNS * GITHUB_GRAPH_ROWS - startDayOfWeek - visibleDays.length, 0);

  return (
    <div
      className="grid h-full place-content-center gap-1.5"
      style={{
        gridTemplateColumns: `repeat(${GITHUB_FIXED_COLUMNS}, 20px)`,
        gridTemplateRows: `repeat(${GITHUB_GRAPH_ROWS}, auto)`,
        gridAutoFlow: "column",
      }}
      aria-hidden
    >
      {Array.from({ length: startDayOfWeek }).map((_, index) => (
        <div key={`empty-start-${index}`} className="aspect-square" />
      ))}

      {visibleDays.map((day) => {
        const intensity = getGitHubIntensityLevel(day.count, maxCount);
        return (
          <div
            key={`${day.date.toISOString()}-${day.count}`}
            className="contribution-cell aspect-square rounded-[6px]"
            data-intensity={intensity}
            style={{ backgroundColor: colorScale[intensity] }}
          />
        );
      })}

      {Array.from({ length: trailingCells }).map((_, index) => (
        <div key={`empty-end-${index}`} className="aspect-square" />
      ))}
    </div>
  );
}

function GitHubContributionLegend({ colorScale, mutedTextColor }: { colorScale: string[]; mutedTextColor: string }) {
  return (
    <div className="flex items-center gap-1 text-xs" style={{ color: mutedTextColor }}>
      <span>Less</span>
      {colorScale.map((color, index) => (
        <div
          key={index}
          className="contribution-legend-swatch h-3 w-3 rounded-[3px]"
          data-intensity={index}
          style={{ backgroundColor: color }}
        />
      ))}
      <span>More</span>
    </div>
  );
}

// Preview component that mirrors the actual templates
function TemplatePreview({
  template,
  values,
  isLoading,
}: {
  template: string;
  values: Record<string, string>;
  isLoading: boolean;
}) {
  const bgColor = values.bg_color || "#1a1a1a";
  const textColor = values.text_color || "#ffffff";
  const hasImage = Boolean(values.image_url);
  const hasLogo = Boolean(values.logo_url);
  const title = values.title || "Your Title Here";
  const description = values.description || "";
  const portfolioInitial = title.trim().charAt(0).toUpperCase();
  const fonts = TEMPLATE_FONTS[template] || TEMPLATE_FONTS.page;
  const isLandingPage = LANDING_PAGE_TYPES.includes(template);

  const formatDate = (value?: string, options?: Intl.DateTimeFormatOptions) => {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString("en-US", options);
  };

  const publishDate = formatDate(values.publish_date, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const episodeDate = formatDate(values.episode_date, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const showBlogMeta = Boolean(values.author_name || values.author_avatar_url || publishDate);
  const showEventMeta = Boolean(values.event_date || values.event_location);
  const showProductMeta = Boolean(values.unique_selling_point);
  const githubAccentColor = normalizeHexColor(values.accent_color, GITHUB_DEFAULT_ACCENT_COLOR);
  const githubColorScale = buildGitHubColorScale(githubAccentColor, bgColor, textColor);
  const githubGridBgColor = mixHexColors(bgColor, textColor, 0.02);
  const githubGridBorderColor = mixHexColors(bgColor, textColor, 0.15);
  const githubCellShadowColor = mixHexColors(bgColor, textColor, 0.12);
  const githubMutedTextColor = mixHexColors(bgColor, textColor, 0.6);
  const githubContainerShadow = `0 25px 50px -12px ${mixHexColors(bgColor, textColor, 0.05)}`;

  const parsedGitHubContributions = parseGitHubContributions(values.contributions);
  const githubContributions =
    parsedGitHubContributions.length > 0 ? parsedGitHubContributions : GITHUB_SAMPLE_CONTRIBUTIONS;
  const githubTotalChanges = computeGitHubTotalChangesLastWeek(githubContributions);
  const githubTitle = values.title?.trim() || "GitHub";
  const githubSubtitle =
    values.description?.trim() || `${githubTotalChanges.toLocaleString()} code changes in the last 7 days.`;
  const githubContributionTheme = {
    "--contribution-color-0": githubColorScale[0],
    "--contribution-color-1": githubColorScale[1],
    "--contribution-color-2": githubColorScale[2],
    "--contribution-color-3": githubColorScale[3],
    "--contribution-color-4": githubColorScale[4],
    "--contribution-box-shadow": `inset 0 0 0 1px ${githubCellShadowColor}`,
  } as CSSProperties;

  if (isLoading) {
    return (
      <div className="aspect-[1.91/1] w-full overflow-hidden rounded-lg border">
        <Skeleton className="h-full w-full" />
      </div>
    );
  }

  const heroImage = hasImage ? (
    <div className="relative h-full w-full overflow-hidden">
      <img
        src={values.image_url}
        alt="Hero"
        className="absolute inset-0 h-full w-full object-cover"
        onError={(e) => (e.currentTarget.style.display = "none")}
      />
    </div>
  ) : null;

  return (
    <div
      className="relative aspect-[1200/630] w-full overflow-hidden rounded-lg border"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      {template === "github" ? (
        <div className="flex h-full flex-col p-4" style={githubContributionTheme}>
          <div
            className="flex items-center gap-2 text-sm"
            style={{ fontFamily: fonts.body, color: githubMutedTextColor }}
          >
            <Github className="h-6 w-6 opacity-90" />
            <span className="text-[30px] leading-none font-semibold" style={{ color: textColor }}>
              {githubTitle}
            </span>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <p className="text-sm" style={{ fontFamily: fonts.body, color: githubMutedTextColor }}>
              {githubSubtitle}
            </p>
            <GitHubContributionLegend colorScale={githubColorScale} mutedTextColor={githubMutedTextColor} />
          </div>

          <div
            className="mt-6 min-h-0 rounded-xl border p-6 shadow-2xl"
            style={{
              backgroundColor: githubGridBgColor,
              borderColor: githubGridBorderColor,
              boxShadow: githubContainerShadow,
            }}
          >
            <GitHubContributionGraph days={githubContributions} colorScale={githubColorScale} />
          </div>
        </div>
      ) : template === "blog_post" ? (
        hasImage ? (
          <div className="absolute inset-0 grid h-full grid-cols-[minmax(0,1fr)_60%]">
            <div className="flex min-w-0 flex-col overflow-hidden pt-[5%] pr-[2.5%] pb-[7.6%] pl-[4%] break-words">
              <div className="flex h-full min-w-0 flex-col justify-around">
                {hasLogo && (
                  <div className="flex justify-start">
                    <img
                      src={values.logo_url}
                      alt="Logo"
                      className="max-h-10 w-auto max-w-[12rem]"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>
                )}
                <div className="mb-[1.9%] flex min-w-0 flex-col gap-[5%]">
                  <h2
                    className="line-clamp-3 leading-[1.15] font-bold tracking-tight text-balance"
                    style={{
                      fontFamily: fonts.heading,
                      fontSize: "36px",
                      color: textColor,
                    }}
                  >
                    {title}
                  </h2>
                </div>

                {showBlogMeta && (
                  <div
                    className="flex items-center gap-3"
                    style={{
                      fontFamily: fonts.body,
                      fontSize: "16px",
                      color: textColor,
                    }}
                  >
                    {values.author_avatar_url && (
                      <img
                        src={values.author_avatar_url}
                        alt={values.author_name || "Author"}
                        className="h-10 w-10 rounded-full border-4"
                        style={{ borderColor: textColor }}
                      />
                    )}
                    <div className="flex flex-col gap-0.5">
                      {values.author_name && (
                        <span className="font-semibold whitespace-nowrap" style={{ color: textColor }}>
                          {values.author_name}
                        </span>
                      )}
                      {publishDate && (
                        <span className="whitespace-nowrap opacity-80" style={{ color: textColor }}>
                          {publishDate}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {heroImage}
          </div>
        ) : (
          <div className="relative z-10 h-full overflow-hidden">
            {/* Logo in top-left corner */}
            {hasLogo && (
              <div className="absolute top-[5%] left-[5%]">
                <img
                  src={values.logo_url}
                  alt="Logo"
                  className="min-h-6 w-auto max-w-16"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              </div>
            )}

            {/* Centered content */}
            <div className="flex h-full flex-col items-center px-[4%] pt-[7.6%] text-center">
              {/* Author avatar */}
              {values.author_avatar_url && (
                <img
                  src={values.author_avatar_url}
                  alt={values.author_name || "Author"}
                  className="mb-2 h-12 w-12 rounded-full border-4"
                  style={{ borderColor: textColor }}
                />
              )}

              {/* Author name (uppercase) */}
              {values.author_name && (
                <span
                  className="mb-[4%] text-[14px] font-semibold tracking-widest uppercase"
                  style={{ fontFamily: fonts.body, color: textColor }}
                >
                  {values.author_name}
                </span>
              )}

              {/* Blog date (smaller, above title) */}
              {publishDate && (
                <span className="mb-2 text-[12px] opacity-70" style={{ fontFamily: fonts.body, color: textColor }}>
                  {publishDate}
                </span>
              )}

              {/* Title */}
              <h2
                className="max-w-[83%] leading-[1.1] font-bold tracking-tight text-balance"
                style={{
                  fontFamily: fonts.heading,
                  fontSize: "42px",
                  color: textColor,
                }}
              >
                {title}
              </h2>
            </div>
          </div>
        )
      ) : template === "podcast" ? (
        hasImage ? (
          <div className="absolute inset-0 grid h-full grid-cols-[minmax(0,1fr)_62.5%]">
            <div className="flex min-w-0 flex-col overflow-hidden pt-[5%] pr-[2.5%] pb-[7.6%] pl-[4%] break-words">
              <div className="flex h-full min-w-0 flex-col justify-around">
                {hasLogo && (
                  <div className="flex justify-start">
                    <img
                      src={values.logo_url}
                      alt="Logo"
                      className="min-h-6 w-auto max-w-24"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>
                )}
                <h2
                  className="leading-[1.15] font-bold tracking-tight text-balance"
                  style={{
                    fontFamily: fonts.heading,
                    fontSize: "36px",
                    color: textColor,
                  }}
                >
                  {title}
                </h2>

                <div
                  className="flex items-center gap-3"
                  style={{
                    fontFamily: fonts.body,
                    fontSize: "16px",
                    color: textColor,
                  }}
                >
                  {episodeDate && (
                    <span
                      className="rounded-md border-2 px-2 py-1 font-semibold"
                      style={{ borderColor: textColor, color: textColor }}
                    >
                      {episodeDate}
                    </span>
                  )}
                  <div className="flex items-center gap-3">
                    <svg className="h-5 w-5" style={{ color: textColor }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                    </svg>
                    <span className="font-medium" style={{ color: textColor }}>
                      Listen Now
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {heroImage}
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col px-[4%] pt-[7.6%] pb-[11.4%]">
            <div className="flex h-full flex-col items-center justify-between gap-[5%] text-center">
              {hasLogo && (
                <img
                  src={values.logo_url}
                  alt="Logo"
                  className="min-h-6 w-auto max-w-24"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}
              <h2
                className="leading-[1.15] font-bold tracking-tight text-balance"
                style={{ fontFamily: fonts.heading, fontSize: "36px", color: textColor }}
              >
                {title}
              </h2>
              <div
                className="flex flex-col items-center gap-3"
                style={{
                  fontFamily: fonts.body,
                  fontSize: "20px",
                  color: textColor,
                }}
              >
                {episodeDate && (
                  <span
                    className="rounded-md border-2 px-2 py-1 font-semibold"
                    style={{ borderColor: textColor, color: textColor }}
                  >
                    {episodeDate}
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <svg className="h-5 w-5" style={{ color: textColor }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                  </svg>
                  <span className="font-medium" style={{ color: textColor }}>
                    Listen Now
                  </span>
                </div>
              </div>
            </div>
          </div>
        )
      ) : template === "book" ? (
        hasImage ? (
          <div className="absolute inset-0 grid h-full grid-cols-[minmax(0,1fr)_42%]">
            <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden pt-[5%] pr-[2.5%] pb-[7.6%] pl-[4%] break-words">
              <div className="flex h-full min-h-0 flex-col justify-around">
                {hasLogo && (
                  <div className="flex justify-start">
                    <img
                      src={values.logo_url}
                      alt="Logo"
                      className="min-h-6 w-auto max-w-32"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>
                )}
                <div className="flex min-h-0 shrink flex-col justify-center">
                  {values.series_number && (
                    <div
                      className="tracking-[0.3em] uppercase opacity-90"
                      style={{
                        fontFamily: fonts.body,
                        fontSize: "12px",
                        color: textColor,
                      }}
                    >
                      Book #{values.series_number}
                    </div>
                  )}
                  <h2
                    className="line-clamp-3 leading-[1.15] font-bold tracking-tight text-balance"
                    style={{
                      fontFamily: fonts.heading,
                      fontSize: "36px",
                      color: textColor,
                    }}
                  >
                    {values.title || title}
                  </h2>
                  {description && (
                    <p
                      className="line-clamp-3 leading-relaxed text-balance opacity-85"
                      style={{
                        fontFamily: fonts.body,
                        fontSize: "16px",
                        color: textColor,
                      }}
                    >
                      {description}
                    </p>
                  )}
                </div>
                {values.author_name && (
                  <div
                    className="mt-auto shrink-0 pt-2 italic opacity-90"
                    style={{
                      fontFamily: fonts.body,
                      fontSize: "16px",
                      color: textColor,
                    }}
                  >
                    by {values.author_name}
                  </div>
                )}
              </div>
            </div>
            <div className="relative flex h-full min-w-0 items-center justify-center overflow-hidden">
              <div className="relative aspect-[2/3] h-[80%] max-h-full max-w-full">
                <img
                  src={values.image_url}
                  alt="Book cover"
                  className="h-full w-full object-cover"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-[4%] text-center">
            <div className="py-[3%]" style={{ borderColor: textColor }}>
              {values.series_number && (
                <div
                  className="mb-[3%] tracking-[0.3em] uppercase opacity-80"
                  style={{
                    fontFamily: fonts.body,
                    fontSize: "12px",
                    color: textColor,
                  }}
                >
                  Book #{values.series_number}
                </div>
              )}
              <h2
                className="leading-[1.15] font-bold tracking-tight text-balance"
                style={{
                  fontFamily: fonts.heading,
                  fontSize: "36px",
                  color: textColor,
                }}
              >
                {values.title || title}
              </h2>
              {description && (
                <p
                  className="mt-2 line-clamp-3 leading-relaxed text-balance opacity-85"
                  style={{
                    fontFamily: fonts.body,
                    fontSize: "20px",
                    color: textColor,
                  }}
                >
                  {description}
                </p>
              )}
              {values.author_name && (
                <div
                  className="mt-2 italic opacity-90"
                  style={{
                    fontFamily: fonts.body,
                    fontSize: "16px",
                    color: textColor,
                  }}
                >
                  by {values.author_name}
                </div>
              )}
            </div>
          </div>
        )
      ) : template === "company" ? (
        <div className="absolute inset-0">
          {hasLogo && (
            <div className="absolute top-[4%] left-[4%]">
              <img
                src={values.logo_url}
                alt="Logo"
                className="max-h-10 w-auto max-w-[6rem]"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </div>
          )}

          <div className="flex h-full flex-col items-center justify-center gap-10 px-20 text-center">
            <div className="flex flex-col items-center">
              {values.company_logo_url && (
                <div className="flex h-[96px] w-full items-center justify-center">
                  <img
                    src={values.company_logo_url}
                    alt="Company logo"
                    className="max-h-[72px] w-auto max-w-[240px] object-contain"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              )}
              <h2
                className="leading-[1.08] font-bold tracking-tight text-balance"
                style={{
                  fontFamily: fonts.heading,
                  fontSize: "48px",
                  color: textColor,
                }}
              >
                {values.title}
              </h2>
            </div>
            {description && (
              <p
                className="inline-flex max-w-[900px] items-center rounded-full px-8 py-4 text-[48px] leading-tight font-bold"
                style={{
                  fontFamily: fonts.body,
                  fontSize: "30px",
                  color: bgColor,
                  backgroundColor: textColor,
                }}
              >
                {description}
              </p>
            )}
          </div>
        </div>
      ) : template === "portfolio" ? (
        hasImage ? (
          <div className="absolute inset-0 grid h-full grid-cols-[minmax(0,1fr)_62.5%]">
            <div className="flex min-w-0 flex-col overflow-hidden pt-[5%] pr-[2.5%] pb-[7.6%] pl-[4%] break-words">
              <div className="flex h-full min-w-0 flex-col justify-around">
                <div className="flex justify-start">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-full border-2"
                    style={{ borderColor: textColor }}
                  >
                    {hasLogo ? (
                      <img
                        src={values.logo_url}
                        alt="Logo"
                        className="h-full w-full rounded-full object-contain"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    ) : (
                      <span
                        className="font-semibold"
                        style={{
                          fontFamily: fonts.heading,
                          fontSize: "24px",
                          color: textColor,
                        }}
                      >
                        {portfolioInitial}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mb-[1.9%] flex flex-col gap-[5%]">
                  <h2
                    className="line-clamp-3 leading-[1.15] font-bold tracking-tight text-balance"
                    style={{
                      fontFamily: fonts.heading,
                      fontSize: "36px",
                      color: textColor,
                    }}
                  >
                    {title}
                  </h2>
                  {description && (
                    <p
                      className="line-clamp-2 leading-relaxed text-balance opacity-90"
                      style={{
                        fontFamily: fonts.body,
                        fontSize: "16px",
                        color: textColor,
                      }}
                    >
                      {description}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {heroImage}
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col px-[4%] pt-[7.6%] pb-[11.4%]">
            <div className="flex h-full flex-col items-center justify-between gap-[5%]">
              <div
                className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-4"
                style={{ borderColor: textColor }}
              >
                {hasLogo ? (
                  <img
                    src={values.logo_url}
                    alt="Logo"
                    className="h-full w-full rounded-full object-contain"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <span
                    className="font-semibold"
                    style={{
                      fontFamily: fonts.heading,
                      fontSize: "36px",
                    }}
                  >
                    {portfolioInitial}
                  </span>
                )}
              </div>
              <div className="flex flex-col items-center gap-[7.6%] text-center">
                <h2
                  className="leading-[1.15] font-bold tracking-tight text-balance"
                  style={{
                    fontFamily: fonts.heading,
                    fontSize: "36px",
                    color: textColor,
                  }}
                >
                  {title}
                </h2>
                {description && (
                  <p
                    className="leading-relaxed text-balance opacity-90"
                    style={{
                      fontFamily: fonts.body,
                      fontSize: "20px",
                      color: textColor,
                    }}
                  >
                    {description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )
      ) : (
        <div className={`absolute inset-0 grid h-full ${hasImage ? "grid-cols-[minmax(0,1fr)_60%]" : ""}`}>
          <div
            className={`flex min-w-0 flex-col overflow-hidden break-words ${
              hasImage ? "pt-[5%] pr-[2.5%] pb-[7.6%] pl-[4%]" : "px-[4%] pt-[7.6%] pb-[11.4%]"
            }`}
          >
            <div
              className={`${hasImage ? "flex h-full min-w-0 flex-col justify-around" : "flex h-full flex-col justify-between gap-[5%]"}`}
            >
              {hasLogo && (
                <div className={hasImage ? "flex justify-start" : ""}>
                  <img
                    src={values.logo_url}
                    alt="Logo"
                    className="max-h-10 w-auto max-w-[12rem]"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              )}
              <div
                className={`flex min-w-0 flex-col ${hasImage ? "gap-[5%]" : "gap-[7.6%]"} ${!description && isLandingPage ? "mb-[3.8%]" : "mb-[1.9%]"}`}
              >
                <h2
                  className="line-clamp-3 leading-[1.15] font-bold tracking-tight text-balance"
                  style={{
                    fontFamily: fonts.heading,
                    fontSize: hasImage ? "36px" : "36px",
                    color: textColor,
                  }}
                >
                  {title}
                </h2>
                {isLandingPage && description && (
                  <p
                    className="line-clamp-3 leading-relaxed text-balance opacity-90"
                    style={{
                      fontFamily: fonts.body,
                      fontSize: hasImage ? "16px" : "20px",
                      color: textColor,
                    }}
                  >
                    {description}
                  </p>
                )}
              </div>

              {template === "event" && showEventMeta && (
                <div
                  className={`flex flex-col gap-1.5 ${hasImage ? "" : "items-center"}`}
                  style={{ fontFamily: fonts.body, color: textColor }}
                >
                  {values.event_date && (
                    <div
                      className="flex items-center gap-1.5 font-bold"
                      style={{ fontSize: hasImage ? "16px" : "20px", color: textColor }}
                    >
                      <span className="flex h-[1lh] items-center">📅</span>
                      {values.event_date}
                    </div>
                  )}
                  {values.event_location && (
                    <div
                      className="flex items-start gap-1.5 font-semibold"
                      style={{ fontSize: hasImage ? "16px" : "20px", color: textColor }}
                    >
                      <span className="flex h-[1lh] items-center">📍</span>
                      {values.event_location}
                    </div>
                  )}
                </div>
              )}

              {template === "product" && showProductMeta && (
                <div
                  className={`inline-flex w-fit max-w-[80%] items-center rounded-2xl px-4 py-2 leading-tight font-bold ${
                    hasImage ? "" : "mx-auto"
                  }`}
                  style={{
                    backgroundColor: textColor,
                    color: bgColor,
                    fontFamily: fonts.body,
                    fontSize: hasImage ? "16px" : "20px",
                  }}
                >
                  {values.unique_selling_point}
                </div>
              )}
            </div>
          </div>
          {heroImage}
        </div>
      )}
    </div>
  );
}

export default function DocsIndex({ templates }: Props) {
  const [copied, setCopied] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("page");
  const [formValues, setFormValues] = useState<Record<string, string>>(TEMPLATE_DEFAULTS.page);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [domainParam, setDomainParam] = useState("yourdomain.com");
  const debounceRef = useRef<number | null>(null);
  const issuedAt = useMemo(() => Math.floor(Date.now() / 1000), []);

  // Get current template config
  const templateList = templates ?? [];
  const currentTemplate = useMemo(
    () => templateList.find((t) => t.name === selectedTemplate) ?? templateList[0],
    [selectedTemplate, templateList],
  );

  // Handle template change
  const handleTemplateChange = (template: string) => {
    setSelectedTemplate(template);
    setIsPreviewLoading(true);
    setFormValues(TEMPLATE_DEFAULTS[template] || {});
    setTimeout(() => setIsPreviewLoading(false), 300);
  };

  // Handle input change with debounced preview update
  const handleInputChange = useCallback((field: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));

    // Debounce preview loading state
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setIsPreviewLoading(true);
    debounceRef.current = window.setTimeout(() => {
      setIsPreviewLoading(false);
    }, 150);
  }, []);

  const copyAsMarkdown = async () => {
    const response = await fetch("/docs.md");
    const markdown = await response.text();
    await navigator.clipboard.writeText(markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const cursorPromptLink = useMemo(() => buildCursorPromptLink(OG_PILOT_CURSOR_PROMPT), []);

  const apiBaseUrl = "https://ogpilot.com/api/v1/images";
  const apiRequestExample = `POST ${apiBaseUrl}
Content-Type: application/json

{"token":"YOUR_JWT_TOKEN"}`;

  const jwtPayload = useMemo(() => {
    const payload: Record<string, string | number> = {
      iss: domainParam || "yourdomain.com",
      sub: apiKey ? apiKey.slice(0, 8) : "YOUR_KEY",
      iat: issuedAt,
      template: selectedTemplate,
    };

    Object.entries(formValues).forEach(([key, value]) => {
      if (value) {
        if (key === "quality" || key === "max_bytes") {
          const numericValue = Number.parseInt(value, 10);
          payload[key] = Number.isNaN(numericValue) ? value : numericValue;
        } else {
          payload[key] = value;
        }
      }
    });

    return payload;
  }, [apiKey, domainParam, formValues, issuedAt, selectedTemplate]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const storedApiKey = window.localStorage.getItem("og_pilot_api_key");
    const storedDomain = window.localStorage.getItem("og_pilot_domain");
    if (storedApiKey) setApiKey(storedApiKey);
    if (storedDomain) setDomainParam(storedDomain);
  }, []);

  useEffect(() => {
    window.localStorage.setItem("og_pilot_api_key", apiKey);
  }, [apiKey]);

  useEffect(() => {
    window.localStorage.setItem("og_pilot_domain", domainParam);
  }, [domainParam]);

  return (
    <>
      <Head title="API Documentation">
        <link rel="preconnect" href="https://fonts.bunny.net" />
        {TEMPLATE_FONT_STYLESHEETS.map((href) => (
          <link key={href} rel="stylesheet" href={href} />
        ))}
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 pb-24 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <PublicHeader />

        <div
          className={cn(
            "mx-auto flex w-full max-w-6xl flex-col gap-12 px-4 pt-12 pb-16 sm:pt-20",
            CARD_SURFACE_CLASSNAME,
          )}
        >
          <section className="space-y-6">
            <Badge
              variant="outline"
              className="border-orange-200 bg-orange-50 text-orange-600 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-200"
            >
              Developer Documentation
            </Badge>
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-balance text-gray-900 md:text-5xl lg:text-6xl dark:text-white">
                OG Pilot API Documentation
              </h1>
              <p className="text-base text-gray-600 md:text-lg dark:text-gray-300">
                Generate beautiful dynamic Open Graph images programmatically with signed requests and ready-to-use
                templates.
              </p>
            </div>

            <ul className="space-y-4 text-base text-gray-600 lg:text-lg dark:text-gray-300">
              {HERO_HIGHLIGHTS.map((highlight) => (
                <li key={highlight.text} className="flex gap-3">
                  <span aria-hidden="true">{highlight.emoji}</span>
                  <span>{highlight.text}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                asChild
                variant="outline"
                className="h-11 gap-2 rounded-md border-orange-200 bg-white px-5 text-base font-semibold text-orange-700 shadow transition-colors duration-200 hover:bg-orange-50 hover:text-orange-800 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none dark:border-orange-500/40 dark:bg-gray-900 dark:text-orange-200 dark:hover:bg-orange-500/10"
              >
                <a href={cursorPromptLink}>
                  <img src="/cursor/logo-dark.svg" alt="Cursor logo" className="hidden h-4 w-4 dark:block" />
                  <img src="/cursor/logo-light.svg" alt="Cursor logo" className="h-4 w-4 dark:hidden" />
                  Open in Cursor
                </a>
              </Button>
              <Button
                onClick={() => {
                  void copyAsMarkdown();
                }}
                className="h-11 gap-2 rounded-md bg-orange-500 px-5 text-base font-semibold text-white shadow transition-colors duration-200 hover:bg-orange-600 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Copy for LLMs
                  </>
                )}
              </Button>
              {HERO_LINKS.map((link) => (
                <Button
                  key={link.href}
                  variant="outline"
                  asChild
                  className="border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800 dark:border-orange-500/40 dark:text-orange-200 dark:hover:bg-orange-500/10"
                >
                  <a href={link.href}>{link.label}</a>
                </Button>
              ))}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300">
              Framework guides:{" "}
              {FRAMEWORK_GUIDES.map((guide, index) => (
                <span key={guide.href}>
                  <a href={guide.href} className="font-medium text-orange-700 hover:underline dark:text-orange-300">
                    {guide.label}
                  </a>
                  {index < FRAMEWORK_GUIDES.length - 1 ? " · " : ""}
                </span>
              ))}
            </p>
          </section>

          <section className="relative">
            <div
              className="absolute -top-6 right-0 bottom-6 left-0 z-0 rounded-3xl bg-gradient-to-br from-orange-400 via-orange-300 to-orange-500 opacity-80 blur-2xl"
              aria-hidden="true"
            />
            <Card id="live-preview" className="relative z-10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Live Template Preview
                </CardTitle>
                <CardDescription>Customize the form below and see your OG image update in real-time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-8 lg:grid-cols-[1fr_1.5fr]">
                  {/* Form */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="api_key">OG Pilot API Key (signing secret)</Label>
                      <Input
                        id="api_key"
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="ogpilot-your-api-key"
                      />
                      <p className="text-muted-foreground text-xs">
                        Used locally to derive <code>sub</code>. Stored only in your browser.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="domain">Domain *</Label>
                      <Input
                        id="domain"
                        value={domainParam}
                        onChange={(e) => setDomainParam(e.target.value)}
                        placeholder="yourdomain.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="template-select">Template</Label>
                      <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                        <SelectTrigger id="template-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {templateList.map((t) => (
                            <SelectItem key={t.name} value={t.name}>
                              {t.name} {t.default && "(default)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-xs">{currentTemplate?.description}</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={formValues.title || ""}
                        onChange={(e) => handleInputChange("title", e.target.value)}
                        placeholder="Your page title"
                      />
                    </div>

                    {(selectedTemplate === "page" ||
                      selectedTemplate === "book" ||
                      selectedTemplate === "company" ||
                      selectedTemplate === "github") && (
                      <div className="space-y-2">
                        <Label htmlFor="description">Description {selectedTemplate === "github" ? "*" : ""}</Label>
                        <Textarea
                          id="description"
                          value={formValues.description || ""}
                          onChange={(e) => handleInputChange("description", e.target.value)}
                          placeholder={
                            selectedTemplate === "github"
                              ? "328 code changes in the last 7 days."
                              : "Optional description"
                          }
                          rows={2}
                        />
                      </div>
                    )}

                    {selectedTemplate !== "github" && (
                      <div className="space-y-2">
                        <Label htmlFor="logo_url">Logo URL</Label>
                        <Input
                          id="logo_url"
                          type="url"
                          value={formValues.logo_url || ""}
                          onChange={(e) => handleInputChange("logo_url", e.target.value)}
                          placeholder="https://example.com/logo.png"
                        />
                      </div>
                    )}

                    {selectedTemplate === "company" ? (
                      <div className="space-y-2">
                        <Label htmlFor="company_logo_url">Company Logo URL</Label>
                        <Input
                          id="company_logo_url"
                          type="url"
                          value={formValues.company_logo_url || ""}
                          onChange={(e) => handleInputChange("company_logo_url", e.target.value)}
                          placeholder="https://example.com/company-logo.png"
                        />
                      </div>
                    ) : selectedTemplate !== "github" ? (
                      <div className="space-y-2">
                        <Label htmlFor="image_url">Hero Image URL</Label>
                        <Input
                          id="image_url"
                          type="url"
                          value={formValues.image_url || ""}
                          onChange={(e) => handleInputChange("image_url", e.target.value)}
                          placeholder="https://example.com/hero.jpg"
                        />
                      </div>
                    ) : null}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="bg_color">Background</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={formValues.bg_color || "#1a1a1a"}
                            onChange={(e) => handleInputChange("bg_color", e.target.value)}
                            className="h-9 w-12 cursor-pointer p-1"
                          />
                          <Input
                            id="bg_color"
                            value={formValues.bg_color || ""}
                            onChange={(e) => handleInputChange("bg_color", e.target.value)}
                            placeholder="#1a1a1a"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="text_color">Text</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={formValues.text_color || "#ffffff"}
                            onChange={(e) => handleInputChange("text_color", e.target.value)}
                            className="h-9 w-12 cursor-pointer p-1"
                          />
                          <Input
                            id="text_color"
                            value={formValues.text_color || ""}
                            onChange={(e) => handleInputChange("text_color", e.target.value)}
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="min-w-0 space-y-2">
                        <Label htmlFor="image_type">Image Type</Label>
                        <Select
                          value={formValues.image_type || undefined}
                          onValueChange={(value) => handleInputChange("image_type", value)}
                        >
                          <SelectTrigger id="image_type" className="w-full">
                            <SelectValue placeholder="Default delivery format" />
                          </SelectTrigger>
                          <SelectContent>
                            {SUPPORTED_IMAGE_TYPES.map((imageType) => (
                              <SelectItem key={imageType} value={imageType}>
                                {imageType}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-muted-foreground text-xs">
                          Optional image delivery format. Use <code>jpeg</code>, <code>png</code>, <code>webp</code>, or{" "}
                          <code>gif</code>.
                        </p>
                      </div>
                      <div className="min-w-0 space-y-2">
                        <Label htmlFor="quality">Quality</Label>
                        <Input
                          id="quality"
                          type="number"
                          min={1}
                          max={100}
                          value={formValues.quality || ""}
                          onChange={(e) => handleInputChange("quality", e.target.value)}
                          placeholder="82"
                        />
                        <p className="text-muted-foreground text-xs">Optional image quality from 1 to 100.</p>
                      </div>
                      <div className="min-w-0 space-y-2">
                        <Label htmlFor="max_bytes">Max Bytes</Label>
                        <Input
                          id="max_bytes"
                          type="number"
                          min={1}
                          value={formValues.max_bytes || ""}
                          onChange={(e) => handleInputChange("max_bytes", e.target.value)}
                          placeholder="220000"
                        />
                        <p className="text-muted-foreground text-xs">Optional byte cap for the delivered image.</p>
                      </div>
                    </div>

                    {selectedTemplate === "github" && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="accent_color">Accent Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={normalizeHexColor(formValues.accent_color, GITHUB_DEFAULT_ACCENT_COLOR)}
                              onChange={(e) => handleInputChange("accent_color", e.target.value)}
                              className="h-9 w-12 cursor-pointer p-1"
                            />
                            <Input
                              id="accent_color"
                              value={formValues.accent_color || ""}
                              onChange={(e) => handleInputChange("accent_color", e.target.value)}
                              placeholder={GITHUB_DEFAULT_ACCENT_COLOR}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contributions">Contributions JSON *</Label>
                          <Textarea
                            id="contributions"
                            value={formValues.contributions || ""}
                            onChange={(e) => handleInputChange("contributions", e.target.value)}
                            rows={9}
                            className="font-mono text-xs"
                          />
                          <p className="text-muted-foreground text-xs">
                            Each entry should look like <code>{`{ "date": "2026-02-15", "count": 4 }`}</code>. One entry
                            maps to one day cell. We render the last <code>{GITHUB_VISIBLE_DAYS}</code> days and
                            auto-fill missing days with <code>count: 0</code>. Grid is fixed at{" "}
                            <code>{GITHUB_FIXED_COLUMNS}</code> columns and <code>{GITHUB_GRAPH_ROWS}</code> rows with
                            empty placeholders for alignment.
                          </p>
                        </div>
                      </>
                    )}

                    {/* Template-specific fields */}
                    {selectedTemplate === "blog_post" && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="author_name">Author Name</Label>
                          <Input
                            id="author_name"
                            value={formValues.author_name || ""}
                            onChange={(e) => handleInputChange("author_name", e.target.value)}
                            placeholder="Author name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="author_avatar_url">Author Avatar URL</Label>
                          <Input
                            id="author_avatar_url"
                            type="url"
                            value={formValues.author_avatar_url || ""}
                            onChange={(e) => handleInputChange("author_avatar_url", e.target.value)}
                            placeholder="https://example.com/avatar.png"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="publish_date">Publish Date</Label>
                          <Input
                            id="publish_date"
                            type="date"
                            value={formValues.publish_date || ""}
                            onChange={(e) => handleInputChange("publish_date", e.target.value)}
                          />
                        </div>
                      </>
                    )}

                    {selectedTemplate === "podcast" && (
                      <div className="space-y-2">
                        <Label htmlFor="episode_date">Episode Date</Label>
                        <Input
                          id="episode_date"
                          type="date"
                          value={formValues.episode_date || ""}
                          onChange={(e) => handleInputChange("episode_date", e.target.value)}
                        />
                      </div>
                    )}

                    {selectedTemplate === "product" && (
                      <div className="space-y-2">
                        <Label htmlFor="usp">Unique Selling Point</Label>
                        <Input
                          id="usp"
                          value={formValues.unique_selling_point || ""}
                          onChange={(e) => handleInputChange("unique_selling_point", e.target.value)}
                          placeholder="50% Off Today"
                        />
                      </div>
                    )}

                    {selectedTemplate === "event" && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="event_date">Event Date</Label>
                          <Input
                            id="event_date"
                            value={formValues.event_date || ""}
                            onChange={(e) => handleInputChange("event_date", e.target.value)}
                            placeholder="March 15-17, 2024"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="event_location">Location</Label>
                          <Input
                            id="event_location"
                            value={formValues.event_location || ""}
                            onChange={(e) => handleInputChange("event_location", e.target.value)}
                            placeholder="San Francisco, CA"
                          />
                        </div>
                      </>
                    )}

                    {selectedTemplate === "book" && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="book_author">Author</Label>
                          <Input
                            id="book_author"
                            value={formValues.book_author || ""}
                            onChange={(e) => handleInputChange("book_author", e.target.value)}
                            placeholder="Author name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="book_series">Series Number</Label>
                          <Input
                            id="book_series"
                            type="number"
                            value={formValues.book_series_number || ""}
                            onChange={(e) => handleInputChange("book_series_number", e.target.value)}
                            placeholder="1"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Preview */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Preview</Label>
                      <span className="text-muted-foreground text-xs">1200 × 630px</span>
                    </div>
                    <TemplatePreview template={selectedTemplate} values={formValues} isLoading={isPreviewLoading} />
                    <p className="text-muted-foreground text-xs">
                      This preview simulates how your OG image will appear. The actual API can deliver JPEG, PNG, WEBP,
                      or GIF when <code>image_type</code> is set.
                    </p>

                    <div className="space-y-2">
                      <Label>JWT Payload (sign with your API key)</Label>
                      <div className="bg-muted rounded-lg p-3 text-xs">
                        <code className="whitespace-pre-wrap">{JSON.stringify(jwtPayload, null, 2)}</code>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Sign this payload with HS256 using your API key as the secret. Remove <code>iat</code> for
                        indefinite caching. Use <code>image_type</code> for the delivered file format, not the HTTP
                        response format.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>API Request</Label>
                      <div className="bg-muted rounded-lg p-3 text-xs">
                        <code className="whitespace-pre-wrap">{apiRequestExample}</code>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Getting Started */}
          <Card id="getting-started" className="mb-8">
            <CardHeader>
              <CardTitle>Getting Started</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-inside list-disc space-y-2">
                <li>Sign up at ogpilot.com</li>
                <li>Add your domain and verify ownership via DNS TXT</li>
                <li>Generate an API key in Settings → API Keys</li>
                <li>
                  Follow framework-specific integration guides:{" "}
                  <a href="/nextjs-og-image-generator" className="text-orange-700 hover:underline dark:text-orange-300">
                    Next.js
                  </a>
                  ,{" "}
                  <a href="/rails-og-image-generator" className="text-orange-700 hover:underline dark:text-orange-300">
                    Rails
                  </a>
                  , and{" "}
                  <a href="/astro-og-image-generator" className="text-orange-700 hover:underline dark:text-orange-300">
                    Astro
                  </a>
                  . The{" "}
                  <a href="/astro-og-image" className="text-orange-700 hover:underline dark:text-orange-300">
                    Astro OG image comparison
                  </a>{" "}
                  covers static files, build-time endpoints, and this API. WordPress sites that need to change the
                  social image should read{" "}
                  <a href="/change-og-image-wordpress" className="text-orange-700 hover:underline dark:text-orange-300">
                    how to change the OG image in WordPress
                  </a>
                  . WordPress tag names are explained in{" "}
                  <a
                    href="/open-graph-protocol-wordpress"
                    className="text-orange-700 hover:underline dark:text-orange-300"
                  >
                    the Open Graph protocol on WordPress
                  </a>
                  .
                </li>
                <li>Install the appropriate SDK for your language:</li>
                <ul className="ml-4 list-inside list-[circle] space-y-2">
                  <li>
                    <strong>Ruby:</strong> Install the{" "}
                    <a href="https://github.com/sunergos-ro/og_pilot_ruby" target="_blank" rel="noopener noreferrer">
                      <code className="bg-muted rounded-md px-1 py-0.5 text-orange-600 hover:text-orange-700 hover:decoration-orange-600">
                        &quot;og_pilot_ruby&quot;
                      </code>
                    </a>{" "}
                    gem (<code>~&gt; {SDK_VERSIONS.ruby}</code>) to generate OG images in your application
                  </li>
                  <li>
                    <strong>PHP:</strong> Install the{" "}
                    <a href="https://github.com/sunergos-ro/og-pilot-php" target="_blank" rel="noopener noreferrer">
                      <code className="bg-muted rounded-md px-1 py-0.5 text-orange-600 hover:text-orange-700 hover:decoration-orange-600">
                        &quot;og-pilot-php&quot;
                      </code>
                    </a>
                    package to generate OG images in your application
                  </li>
                  <li>
                    <strong>Python:</strong> Install the{" "}
                    <a href="https://github.com/sunergos-ro/og-pilot-python" target="_blank" rel="noopener noreferrer">
                      <code className="bg-muted rounded-md px-1 py-0.5 text-orange-600 hover:text-orange-700 hover:decoration-orange-600">
                        &quot;og-pilot&quot;
                      </code>
                    </a>
                    package (<code>=={SDK_VERSIONS.python}</code>) to generate OG images in your application
                  </li>
                  <li>
                    <strong>JavaScript:</strong> Install the{" "}
                    <a href="https://github.com/sunergos-ro/og-pilot-js" target="_blank" rel="noopener noreferrer">
                      <code className="bg-muted rounded-md px-1 py-0.5 text-orange-600 hover:text-orange-700 hover:decoration-orange-600">
                        &quot;og-pilot-js&quot;
                      </code>
                    </a>
                    package (<code>^{SDK_VERSIONS.javascript}</code>) to generate OG images in your application
                  </li>
                </ul>
              </ul>
              <p className="text-muted-foreground mt-4 text-xs">SDK versions verified on February 23, 2026.</p>
            </CardContent>
          </Card>

          <Card id="mcp" className="mb-8">
            <CardHeader>
              <CardTitle>MCP Connector</CardTitle>
              <CardDescription>Custom Claude connector and ChatGPT developer mode</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p>
                OG Pilot also has a public remote MCP endpoint at{" "}
                <code className="bg-muted rounded-md px-1 py-0.5">https://ogpilot.com/mcp</code>. Tools:{" "}
                <code className="bg-muted rounded-md px-1 py-0.5">generate_og_image</code>,{" "}
                <code className="bg-muted rounded-md px-1 py-0.5">debug_open_graph</code>,{" "}
                <code className="bg-muted rounded-md px-1 py-0.5">compress_image</code>, and{" "}
                <code className="bg-muted rounded-md px-1 py-0.5">extract_color_palette</code>. Generation takes a page
                URL and returns an Open Graph image. Generation needs a signed-in OG Pilot account with an active plan
                and credits. A trial is not enough. Other sessions get a <code>plan_required</code> error, not a
                checkout. Debug, compress, and palette work without an account and do not spend credits. This is not the
                JWT domain API, and it is not listed in official directories yet.
              </p>
              <p>
                <a href="/docs/mcp" className="text-orange-700 hover:underline dark:text-orange-300">
                  Install the MCP server
                </a>{" "}
                and the{" "}
                <a href="/privacy" className="text-orange-700 hover:underline dark:text-orange-300">
                  Privacy Policy
                </a>
                .
              </p>
            </CardContent>
          </Card>

          {/* How It Works - Architecture */}
          <Card id="how-it-works" className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                How It Works
              </CardTitle>
              <CardDescription>
                JWT signing happens in <strong>your application</strong>, not on OG Pilot servers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="mb-4 text-sm">
                  OG Pilot uses JWT tokens signed with your API key to authenticate requests. This means:
                </p>
                <ul className="text-muted-foreground list-inside list-disc space-y-1 text-sm">
                  <li>
                    <strong>Your API key never leaves your server</strong> — it&apos;s used only to sign tokens
                  </li>
                  <li>
                    <strong>Each page gets a unique signed request</strong> — generated at render time with dynamic
                    content
                  </li>
                  <li>
                    <strong>Social platforms fetch the image</strong> — when they crawl your og:image meta tag
                  </li>
                </ul>
              </div>

              {/* Flow Diagram */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Integration Flow</Label>
                <div className="bg-muted/30 flex flex-wrap items-center justify-center gap-2 rounded-lg p-4 text-xs sm:text-sm">
                  <div className="rounded-md bg-orange-100 px-3 py-2 font-medium text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
                    Your App
                  </div>
                  <ArrowRight className="text-muted-foreground h-4 w-4" />
                  <div className="bg-muted rounded-md px-3 py-2">Sign JWT</div>
                  <ArrowRight className="text-muted-foreground h-4 w-4" />
                  <div className="bg-muted rounded-md px-3 py-2">POST /api/v1/images</div>
                  <ArrowRight className="text-muted-foreground h-4 w-4" />
                  <div className="bg-muted rounded-md px-3 py-2">CDN Image URL</div>
                  <ArrowRight className="text-muted-foreground h-4 w-4" />
                  <div className="bg-muted rounded-md px-3 py-2">Social Platform</div>
                  <ArrowRight className="text-muted-foreground h-4 w-4" />
                  <div className="rounded-md bg-orange-100 px-3 py-2 font-medium text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
                    OG Pilot CDN
                  </div>
                </div>
              </div>

              {/* Quick Example */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">The Pattern (Ruby)</Label>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                      1. Install gem & configure once
                    </p>
                    <pre className="bg-muted overflow-x-auto rounded-lg p-3 text-xs">
                      {`# Gemfile
gem "og_pilot_ruby", "~> ${SDK_VERSIONS.ruby}"

# Set OG_PILOT_API_KEY and OG_PILOT_DOMAIN in your environment variables
# OG_PILOT_API_KEY=ogpilot-your-api-key
# OG_PILOT_DOMAIN=yourdomain.com`}
                    </pre>
                  </div>
                  <div className="space-y-2">
                    <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                      2. Generate images anywhere
                    </p>
                    <pre className="bg-muted overflow-x-auto rounded-lg p-3 text-xs">
                      {`# Use in meta tag
<meta property="og:image"
  content="<%= OgPilotRuby.create_image(title: "My Post Title") %>" />`}
                    </pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Integration Checklist */}
          <Card id="integration-checklist" className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                Integration Checklist
              </CardTitle>
              <CardDescription>Quick reference for setting up OG Pilot in your application</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Circle className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-medium">Install the appropriate library</p>
                    <p className="text-muted-foreground text-sm">
                      Ruby: <code>og_pilot_ruby</code> (<code>~&gt; {SDK_VERSIONS.ruby}</code>)
                      <br />
                      JS: <code>og-pilot-js</code> (<code>^{SDK_VERSIONS.javascript}</code>)
                      <br />
                      Python: <code>og-pilot</code> (<code>=={SDK_VERSIONS.python}</code>)
                      <br />
                      PHP: <code>sunergos/og-pilot-php</code>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Circle className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-medium">Store API key and domain in environment variables</p>
                    <p className="text-muted-foreground text-sm">
                      <pre className="bg-muted overflow-x-auto rounded-lg p-3 text-xs">
                        {`OG_PILOT_API_KEY=ogpilot-your-api-key
OG_PILOT_DOMAIN=yourdomain.com`}
                      </pre>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Circle className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-medium">Configure the SDK once</p>
                    <p className="text-muted-foreground text-sm">
                      Ruby: <code>OgPilotRuby.configure</code>
                      <br />
                      JS: <code>configure</code>
                      <br />
                      Python: <code>og_pilot.configure</code>
                      <br />
                      PHP: <code>OgPilot::setConfig</code> (or .env in Laravel)
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Circle className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-medium">
                      Add <code>og:image</code> meta tag to your layout
                    </p>
                    <p className="text-muted-foreground text-sm">
                      Ruby: <code>OgPilotRuby.create_image(title: ...)</code>
                      <br />
                      JS: <code>createImage(...)</code>
                      <br />
                      Python: <code>og_pilot.create_image(...)</code>
                      <br />
                      PHP: <code>OgPilot::createImage([...])</code>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Circle className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-medium">Override per-page as needed</p>
                    <p className="text-muted-foreground text-sm">
                      Pass template-specific parameters (author, date, etc.) to customize individual pages
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Authentication */}
          <Card id="authentication" className="mb-8">
            <CardHeader>
              <CardTitle>Authentication</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                All API requests require a signed JWT sent in the JSON request body as <code>token</code>:
              </p>
              <pre className="bg-muted rounded-lg p-4">
                <code>{`POST https://ogpilot.com/api/v1/images
Content-Type: application/json

{"token":"YOUR_JWT_TOKEN"}`}</code>
              </pre>
              <div className="mt-4 space-y-2 text-sm">
                <p className="font-medium">Required JWT claims:</p>
                <ul className="text-muted-foreground list-inside list-disc">
                  <li>
                    <code>iss</code> - Verified domain hostname for your account
                  </li>
                  <li>
                    <code>sub</code> - API key prefix (first 8 characters of your API key)
                  </li>
                </ul>
                <p className="text-muted-foreground">
                  Optional: <code>iat</code> for daily cache busting, plus any image parameters like{" "}
                  <code>template</code>, <code>title</code>, <code>description</code>, <code>path</code>,{" "}
                  <code>image_type</code>, <code>quality</code>, and <code>max_bytes</code>.
                </p>
              </div>
              <p className="text-muted-foreground mt-4 text-sm">
                API keys are account-scoped and can be used with any verified domain on your account.
              </p>
              <p className="text-muted-foreground mt-2 text-sm">
                Domain ownership must be verified via DNS TXT before the API generates images. For local development,
                requests from localhost are allowed in development mode only.
              </p>
            </CardContent>
          </Card>

          {/* Cache Busting */}
          <Card id="cache-busting" className="mb-8">
            <CardHeader>
              <CardTitle>Cache Busting</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-3 text-sm">
              <p>
                If you include <code>iat</code> in the JWT payload, OG Pilot treats the image as cacheable for 24 hours
                from that timestamp.
              </p>
              <p>
                To refresh daily, generate a new JWT with a new <code>iat</code> value. If you omit <code>iat</code>,
                the image will remain cached indefinitely until parameters change.
              </p>
              <p>
                If a new render is queued, OG Pilot serves the last completed image while regeneration happens in the
                background. When no prior image exists, JSON responses return a 202 and HTML/PNG requests redirect to a
                processing placeholder. Failed renders return an error placeholder while OG Pilot retries.
              </p>
            </CardContent>
          </Card>

          {/* Templates Reference */}
          <Card id="templates" className="mb-8">
            <CardHeader>
              <CardTitle>Templates Reference</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {templateList.map((template) => (
                  <div key={template.name} className="border-b pb-6 last:border-0">
                    <h3 className="mb-2 font-semibold">
                      {template.name}
                      {template.default && (
                        <span className="ml-2 rounded bg-orange-100 px-2 py-1 text-xs text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
                          default
                        </span>
                      )}
                    </h3>
                    <p className="text-muted-foreground mb-4 text-sm">{template.description}</p>

                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="pb-2 text-left font-medium">Parameter</th>
                          <th className="pb-2 text-left font-medium">Type</th>
                          <th className="pb-2 text-left font-medium">Required</th>
                          <th className="pb-2 text-left font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(template.parameters ?? []).map((param) => (
                          <tr key={param.name} className="border-b last:border-0">
                            <td className="py-2 font-mono text-xs">{param.name}</td>
                            <td className="py-2">{param.type}</td>
                            <td className="py-2">{param.required ? "Yes" : "No"}</td>
                            <td className="text-muted-foreground py-2">{param.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Code Examples */}
          <div id="code-examples" className="mb-8">
            <CodeExamplesCard
              title="Code Examples"
              description="Use these helper functions to generate dynamic OG images. Pass page-specific values as parameters."
              showApiKeySelector={false}
            />
          </div>

          {/* Error Codes */}
          <div className="grid gap-8">
            <Card id="error-codes">
              <CardHeader>
                <CardTitle>Error Codes</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-mono">400 Bad Request</td>
                      <td className="py-2">Missing or invalid parameters</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">401 Unauthorized</td>
                      <td className="py-2">Invalid JWT token</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">403 Forbidden</td>
                      <td className="py-2">Domain mismatch, not verified, or subscription required</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">404 Not Found</td>
                      <td className="py-2">Not found</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono">429 Too Many Requests</td>
                      <td className="py-2">Rate limited</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </div>

        <FinalCta
          title="Ship branded OG images in your next deploy"
          description="Connect your domain, generate signed requests, and let OG Pilot handle every social preview. One endpoint, all your templates."
          highlights={[
            { icon: "🔐", text: "JWT signing stays on your server" },
            { icon: "⚡️", text: "Templates ready for blogs, products, podcasts, and more" },
          ]}
          submitLabel="Connect domain"
        />

        <SiteFooter />
      </div>
    </>
  );
}
